import { Controller } from "@hotwired/stimulus"

const LEVERAGES = [5, 10, 20, 50, 100, 125]

export default class extends Controller {
  static targets = [
    "principal", "principalInput",
    "leverageSlider", "leverageDisplay",
    "tradesPerDay", "tradesInput",
    "exchangeBtn",
    "dailyRebate", "dailyFeeDisplay",
    "monthlyRebate", "monthlyFeeDisplay",
    "annualRebate", "annualRebate2",
    "annualFee", "rebateBar",
    "exchangeRebateRate", "exchangeName",
    "dailyVolume", "feeRateDisplay"
  ]

  static values = { exchanges: Array }

  connect() {
    this.selectedExchange = this.exchangesValue[0] || { rebateRate: 0.20, feeRate: 0.0005, name: "币安 Binance" }
    this.selectedLeverage = 20
    if (this.hasPrincipalTarget)     this.fillSlider(this.principalTarget)
    if (this.hasLeverageSliderTarget) this.fillSlider(this.leverageSliderTarget)
    if (this.hasTradesPerDayTarget)  this.fillSlider(this.tradesPerDayTarget)
    this.calculate()
  }

  // ── 交易所 ──────────────────────────────────────
  selectExchange(event) {
    const id = event.currentTarget.dataset.exchangeId
    this.selectedExchange = this.exchangesValue.find(e => e.id === id) || this.selectedExchange
    this.exchangeBtnTargets.forEach(b =>
      b.classList.toggle("rc-exch-active", b.dataset.exchangeId === id)
    )
    this.calculate()
  }

  // ── 杠杆滑块 ────────────────────────────────────
  onLeverageSlider(event) {
    const idx = parseInt(event.target.value)
    this.selectedLeverage = LEVERAGES[idx] ?? 20
    if (this.hasLeverageDisplayTarget)
      this.leverageDisplayTarget.textContent = this.selectedLeverage
    this.fillSlider(event.target)
    this.calculate()
  }

  // ── 本金 ────────────────────────────────────────
  onPrincipalSlider(event) {
    const val = Math.max(1, parseInt(event.target.value))
    if (this.hasPrincipalInputTarget) this.principalInputTarget.value = val
    this.fillSlider(event.target)
    this.calculate()
  }

  onPrincipalInput(event) {
    let val = Math.max(1, Math.min(20000, parseInt(event.target.value) || 1000))
    event.target.value = val
    if (this.hasPrincipalTarget) {
      this.principalTarget.value = Math.min(val, parseInt(this.principalTarget.max))
      this.fillSlider(this.principalTarget)
    }
    this.calculate()
  }

  // ── 次/天 ────────────────────────────────────────
  onTradesSlider(event) {
    const val = parseInt(event.target.value)
    if (this.hasTradesInputTarget) this.tradesInputTarget.value = val
    this.fillSlider(event.target)
    this.calculate()
  }

  onTradesInput(event) {
    let val = Math.max(1, Math.min(50, parseInt(event.target.value) || 5))
    event.target.value = val
    if (this.hasTradesPerDayTarget) {
      this.tradesPerDayTarget.value = val
      this.fillSlider(this.tradesPerDayTarget)
    }
    this.calculate()
  }

  // ── 计算 ────────────────────────────────────────
  calculate() {
    // 优先读 input 框，支持超出 slider 上限的本金
    const principal  = this.hasPrincipalInputTarget
      ? (parseFloat(this.principalInputTarget.value) || 1000)
      : (parseFloat(this.principalTarget.value) || 1000)
    const leverage   = this.selectedLeverage || 20
    const trades     = parseInt(this.tradesPerDayTarget.value) || 5
    const ex         = this.selectedExchange || {}
    const rebateRate = ex.rebateRate ?? 0.20
    const feeRate    = (ex.feeRate ?? 0.0005) * 2   // 双边

    const dailyFee      = principal * leverage * feeRate * trades
    const dailyRebate   = dailyFee   * rebateRate
    const monthlyFee    = dailyFee   * 30
    const monthlyRebate = dailyRebate * 30
    const annualFee     = dailyFee   * 365
    const annualRebate  = dailyRebate * 365

    this.set("annualRebate",      annualRebate)
    this.set("annualRebate2",     annualRebate)
    this.set("dailyRebate",       dailyRebate)
    this.set("dailyFeeDisplay",   dailyFee)
    this.set("monthlyRebate",     monthlyRebate)
    this.set("monthlyFeeDisplay", monthlyFee)
    this.set("annualFee",         annualFee)
    this.set("dailyVolume",       principal * leverage * trades)

    if (this.hasFeeRateDisplayTarget)
      this.feeRateDisplayTarget.textContent = ((ex.feeRate ?? 0.0005) * 100).toFixed(2) + "% 单边"

    if (this.hasRebateBarTarget)
      this.rebateBarTarget.style.width = (rebateRate * 100).toFixed(0) + "%"
    if (this.hasExchangeRebateRateTarget)
      this.exchangeRebateRateTarget.textContent = (rebateRate * 100).toFixed(0) + "%"
    if (this.hasExchangeNameTarget)
      this.exchangeNameTarget.textContent = ex.name ?? ""
  }

  // ── 工具 ────────────────────────────────────────
  fillSlider(el) {
    const min = parseFloat(el.min) || 0
    const max = parseFloat(el.max) || 100
    const val = parseFloat(el.value) || 0
    const pct = ((val - min) / (max - min) * 100).toFixed(1) + "%"
    el.style.setProperty("--pct", pct)
  }

  set(name, value) {
    const key = name.charAt(0).toUpperCase() + name.slice(1)
    if (!this["has" + key + "Target"]) return
    const text = this.fmt(value)
    this[name + "Targets"].forEach(el => { el.textContent = text })
  }

  fmt(n) {
    if (n >= 10000) {
      return n.toLocaleString("zh-CN", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " USDT"
    }
    // 去掉尾部多余的零：有小数时保留最多2位，整数直接不显示小数点
    return n.toLocaleString("zh-CN", { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + " USDT"
  }
}
